import { ChevronRight } from "@/components/ui/icons";
import { Pressable, Text, View } from "react-native";

import type { PublicLiveTable } from "@/hooks/usePublicBrowse";
import { useThemeColors } from "@/theme/theme";

/**
 * Asks a visitor which of a vendor's tables they are standing at (ADR-0138).
 *
 * It appears only when the answer is genuinely unknown: someone who arrived at the front door by
 * name or by the shop QR, while two tables are live. A table QR already answers the question, and
 * with one table live there is no question, so the screen sends them straight through.
 *
 * Guessing is the thing to avoid. The two tables hold different stock, take separate Pull Sheets
 * and may not even be at the same show; a customer sent to the wrong one browses a list they
 * cannot buy from and has no reason to suspect it.
 */
export function TableArrivalChooser(
  { tables, onChoose }: {
    tables: readonly PublicLiveTable[];
    onChoose: (eventId: string) => void;
  },
) {
  const colors = useThemeColors();
  return (
    <View testID="table-arrival-chooser" className="gap-3 px-4 py-6" accessibilityRole="menu">
      <Text
        accessibilityRole="header"
        aria-level={2}
        className="font-title text-xl text-foreground"
      >
        Which table are you at?
      </Text>
      <Text className="font-body text-sm text-muted-foreground">
        This shop is running more than one right now, and each one has its own cards.
      </Text>
      <View className="gap-2">
        {tables.map((table) => (
          <Pressable
            key={table.event_id}
            testID={`table-arrival-${table.event_id}`}
            onPress={() => onChoose(table.event_id)}
            accessibilityRole="menuitem"
            accessibilityLabel={`Browse ${table.name}`}
            className="min-h-14 flex-row items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3"
          >
            <Text className="min-w-0 flex-1 font-label text-base text-foreground">
              {table.name}
            </Text>
            <ChevronRight
              size={18}
              color={colors.mutedForeground}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );
}
